/**
 * The account block an event may carry, read out of the OCSF attribute bag.
 *
 * One reader because three screens want it, and because the rule that matters is easy to get wrong
 * separately in each of them: `status` decides whether `tier` says anything, and an unresolved read
 * must never render as the free plan. A machine whose sidebar was collapsed appearing in a report as
 * being on the free plan would be worse than it not appearing, because somebody would act on it.
 */
export interface ObservedAccount {
  /** resolved | not-found | ambiguous | disabled, or whatever a newer agent sends. */
  status: string
  /** Present only when the agent actually resolved one. */
  tier: string | null
  tierRaw: string | null
  userName: string | null
  /** The rule name a tier would have been reported under, when the policy named one. */
  ruleName: string | null
}

export function readAccount(attributesJson: string | null | undefined): ObservedAccount | null {
  if (!attributesJson) return null

  let attributes: Record<string, unknown>
  try {
    attributes = JSON.parse(attributesJson) as Record<string, unknown>
  } catch {
    return null
  }

  const account = attributes.ai_account
  if (account === null || typeof account !== 'object' || Array.isArray(account)) return null

  const fields = account as Record<string, unknown>
  const status = typeof fields.status === 'string' ? fields.status : 'unknown'
  const resolved = status === 'resolved'

  return {
    status,
    // Guarded here as well as on the server. Both sides refusing to invent a plan is the point:
    // this is the one way the feature could actively mislead an operator.
    tier: resolved && typeof fields.tier === 'string' ? fields.tier : null,
    tierRaw: resolved && typeof fields.tier_raw === 'string' ? fields.tier_raw : null,
    userName: typeof fields.user_name === 'string' ? fields.user_name : null,
    ruleName: typeof attributes.ai_account_rule === 'string' ? attributes.ai_account_rule : null
  }
}

/** What the operator reads where a plan would go. Never a plan the agent did not resolve. */
export function describeAccount(account: ObservedAccount): string {
  if (account.tier) return account.tierRaw ?? account.tier
  switch (account.status) {
    case 'not-found':
      return 'could not be read'
    case 'ambiguous':
      return 'more than one account matched'
    case 'disabled':
      return 'not captured by this policy'
    default:
      return 'unknown'
  }
}
